import { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../lib/api";
import { PLATFORMS } from "../lib/constants";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Plus, Upload, Link as LinkIcon, Copy, Trash2, ImageIcon, Eye, X, Loader2 } from "lucide-react";

function PortfolioImage({ path }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/portfolio/image/${path}`, { responseType: "blob" })
      .then((r) => { url = URL.createObjectURL(r.data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [path]);
  if (!src) return <div className="w-full h-full bg-muted animate-pulse" />;
  return <img src={src} alt="portfolio" className="w-full h-full object-cover" />;
}

export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", platform: "Twitch", images: [], is_public: true });
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    api.get("/portfolio").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const handleUpload = async (files) => {
    setUploading(true);
    const paths = [];
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/portfolio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      } catch (e) {
        toast.error(`${file.name} yüklenemedi`);
      }
    }
    setForm((f) => ({ ...f, images: [...f.images, ...paths] }));
    setUploading(false);
    if (paths.length) toast.success(`${paths.length} görsel yüklendi`);
  };

  const removeImage = (path) => {
    setForm((f) => ({ ...f, images: f.images.filter((p) => p !== path) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.images.length === 0) { toast.error("En az bir görsel ekleyin"); return; }
    try {
      await api.post("/portfolio", form);
      toast.success("Portföy öğesi eklendi");
      setOpen(false);
      setForm({ title: "", description: "", platform: "Twitch", images: [], is_public: true });
      load();
    } catch { toast.error("Kaydedilemedi"); }
  };

  const del = async (id) => {
    try { await api.delete(`/portfolio/${id}`); toast.success("Silindi"); load(); }
    catch { toast.error("Silinemedi"); }
  };

  const copyLink = (slug) => {
    const url = `${window.location.origin}/p/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Paylaşım linki kopyalandı");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Vitrin</div>
          <div className="text-2xl font-display font-semibold tracking-tight mt-1">Portföy ({items.length})</div>
          <div className="text-sm text-muted-foreground mt-1">Tamamlanan işlerini paylaşılabilir link ile sergile</div>
        </div>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-portfolio-btn" className="rounded-full"><Plus className="h-4 w-4 mr-2" /> Yeni İş Ekle</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Portföye Yeni İş Ekle</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="portfolio-form">
              <div className="space-y-2">
                <Label>Başlık</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required placeholder="Örn: Twitch overlay tasarımı — GamerX" data-testid="portfolio-title" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Platform</Label>
                  <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                    <SelectTrigger data-testid="portfolio-platform"><SelectValue /></SelectTrigger>
                    <SelectContent>{PLATFORMS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Herkese Açık</Label>
                  <div className="flex items-center h-10 gap-3">
                    <Switch checked={form.is_public} onCheckedChange={(v) => setForm({ ...form, is_public: v })} data-testid="portfolio-public" />
                    <span className="text-sm text-muted-foreground">{form.is_public ? "Paylaşılabilir" : "Gizli"}</span>
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Açıklama</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Kısa iş özeti, kullanılan araçlar..." data-testid="portfolio-description" />
              </div>

              <div className="space-y-2">
                <Label>Görseller</Label>
                <input
                  ref={fileRef}
                  type="file"
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => e.target.files && handleUpload(Array.from(e.target.files))}
                  data-testid="portfolio-file-input"
                />
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full" data-testid="portfolio-upload-btn">
                  {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Yükleniyor...</> : <><Upload className="h-4 w-4 mr-2" /> Görsel Yükle (JPG/PNG, max 10MB)</>}
                </Button>
                {form.images.length > 0 && (
                  <div className="grid grid-cols-4 gap-2 mt-2">
                    {form.images.map((p) => (
                      <div key={p} className="relative aspect-square rounded-lg overflow-hidden border border-border">
                        <PortfolioImage path={p} />
                        <button type="button" onClick={() => removeImage(p)} className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/70 text-white flex items-center justify-center">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="submit" data-testid="save-portfolio-btn" className="rounded-full">Kaydet</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="text-sm text-muted-foreground">Yükleniyor...</div>
      ) : items.length === 0 ? (
        <Card className="p-16 text-center">
          <ImageIcon className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <div className="text-lg font-display font-medium">Henüz portföy öğesi yok</div>
          <div className="text-sm text-muted-foreground mt-1">Tamamladığın işleri ekle ve paylaşılabilir link ile sergilemeye başla.</div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((it) => (
            <Card key={it.id} className="overflow-hidden group hover:-translate-y-[2px] hover:shadow-md transition-transform duration-200" data-testid={`portfolio-card-${it.id}`}>
              <div className="aspect-[4/3] bg-muted relative">
                {it.images[0] ? <PortfolioImage path={it.images[0]} /> : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-10 w-10 text-muted-foreground/40" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className="rounded-full bg-black/70 text-white text-[10px] px-2 py-0.5">{it.platform}</span>
                  {it.is_public
                    ? <span className="rounded-full bg-green-500 text-white text-[10px] px-2 py-0.5">Açık</span>
                    : <span className="rounded-full bg-neutral-500 text-white text-[10px] px-2 py-0.5">Gizli</span>}
                </div>
              </div>
              <div className="p-4">
                <div className="font-medium truncate">{it.title}</div>
                <div className="text-xs text-muted-foreground mt-1 line-clamp-2 min-h-[2rem]">{it.description || "-"}</div>
                <div className="mt-3 flex items-center gap-1">
                  {it.is_public && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => copyLink(it.share_slug)} data-testid={`portfolio-copy-${it.id}`} className="rounded-full flex-1">
                        <Copy className="h-3.5 w-3.5 mr-1" /> Link
                      </Button>
                      <Button size="sm" variant="outline" asChild className="rounded-full">
                        <a href={`/p/${it.share_slug}`} target="_blank" rel="noreferrer" data-testid={`portfolio-view-${it.id}`}>
                          <Eye className="h-3.5 w-3.5" />
                        </a>
                      </Button>
                    </>
                  )}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`portfolio-delete-${it.id}`}>
                        <Trash2 className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Silinsin mi?</AlertDialogTitle>
                        <AlertDialogDescription>"{it.title}" kaydı ve tüm görselleri silinecek.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>İptal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(it.id)}>Sil</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
